// Virtualized infinite jobs table: TanStack Table (headless) over Chakra
// table primitives, TanStack Virtual for the rows. Replaces the legacy
// page's client-side pager — scrolling near the end fetches the next
// cursor page. Virtualization uses spacer rows (not absolute positioning)
// so native table column sizing keeps headers and cells aligned.
//
// Every not-yet-loaded index renders a skeleton row instead of blank
// space: the initial load fills the viewport with skeletons, and while
// more pages exist the virtual count runs PHANTOM_ROWS past the loaded
// data, so fast scrolling meets shimmer instead of white. Headers are the
// shared legacy-ui TableHeaderRows — the same sortable header (styles,
// affordance, right-aligned sort arrow) as every enterprise table.

import { Box, Skeleton, Table } from '@chakra-ui/react';
import {
    fixedRowCellProps,
    getShellScroller,
    quietScrollbarStyles,
    type StandardTableFeatures,
    scrollShellToTop,
    standardTableFeatures,
    TableHeaderRows,
} from '@ionq/legacy-ui';
import {
    type Cell,
    type ColumnDef,
    FlexRender,
    type OnChangeFn,
    type Row,
    type RowData,
    type RowSelectionState,
    type SortingState,
    Subscribe,
    useTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';

type JobsColumns<TData extends RowData> = ColumnDef<
    StandardTableFeatures,
    TData,
    // biome-ignore lint/suspicious/noExplicitAny: TanStack's convention — a column list mixes cell value types
    any
>[];

// Prefetch buffer: keep at least this many loaded rows ahead of the virtual
// window; the effect re-fires as pages land, so a fast scroll chains a few
// pages ("preload ahead"). Tall screens fetch page 2 eagerly on mount —
// extra runway, deliberately.
const PREFETCH_BUFFER_ROWS = 60;
// Fallback runway past the loaded data while more pages exist and the
// dataset's true size is unknown — what a fast scroll runs into instead of
// a hard scroll stop. (With a known total the virtualizer spans the whole
// listing and this never applies.)
const PHANTOM_ROWS = 30;
// First-load skeleton count: enough to fill any viewport.
const INITIAL_SKELETON_ROWS = 40;
// Hoisted: the scroll container re-renders per virtualizer frame — an
// inline object would make Emotion re-serialize it at scroll frequency.
const SCROLLER_CSS = { overflowAnchor: 'none', ...quietScrollbarStyles };

// The virtualizer only OBSERVES the shell panel's scroll, never drives it:
// on attaching to the scroll element it scrolls to its notion of the offset
// (0 before the shell's ScrollRestoration lands on Back — and again from
// StrictMode's effect re-run), which would zero the restored position.
// The shell owns the offset; the table never calls scrollToIndex/Offset.
const NEVER_SCROLL = () => {};
// Overscan sizing: how many extra VIEWPORTS of rows stay mounted on each
// side of the visible window (floor below). Kept modest on purpose: every
// overscanned row is real mount work on the frame budget — a whole extra
// viewport of Chakra cells was what stretched scroll-frame tasks past
// 500ms on tall windows and let flings outrun React into blank frames.
const OVERSCAN_VIEWPORTS = 0.5;
const OVERSCAN_MIN_ROWS = 14;

// Deterministic width variation so skeleton cells read as content of
// varying length (same trick as legacy-ui's table).
const SKELETON_CELL_WIDTHS = ['60%', '85%', '45%'];

// Stable identity for "selection disabled": a fresh {} per render would
// invalidate the controlled rowSelection slice's snapshot cache every pass.
const EMPTY_ROW_SELECTION: RowSelectionState = {};

// Rows are memoized because TanStack Virtual re-renders the table on every
// scroll offset change (synchronously while scrolling): a scroll frame must
// only mount rows entering the window, not re-render every visible Chakra
// cell — that wholesale re-render is what let fast scrolls outrun React and
// flash white over already-loaded rows. Selection doesn't ride these props:
// each row subscribes to its own bit of the rowSelection atom (Subscribe
// below), so a flip re-renders only the affected rows through the memo.
// The cells ride as a prop: row objects are keyed on data alone, so a
// column-def change (the owner's paid-access gate landing) keeps the same
// row but builds new cells — the memo must see them.
type DataRowProps<TData extends RowData> = {
    row: Row<StandardTableFeatures, TData>;
    cells: Cell<StandardTableFeatures, TData, unknown>[];
    rowHeight?: number;
};

function DataRowInner<TData extends RowData>({
    row,
    cells,
    rowHeight,
}: DataRowProps<TData>) {
    return (
        // Per-row selection subscription: re-renders this row when its own
        // rowSelection bit flips, so the cell renderers re-read the same
        // atom via row.getIsSelected(). Subscribe renders no DOM node —
        // <tbody> still sees the plain <tr> structure.
        <Subscribe
            source={row.table.atoms.rowSelection}
            selector={(rowSelection) => !!rowSelection?.[row.id]}
        >
            {(selected) => (
                <Table.Row
                    data-selected={selected || undefined}
                    // Same row hover as legacy-ui's table.
                    _hover={{ bg: { base: 'bg.subtle', _dark: 'bg.hover' } }}
                    h={rowHeight ? `${rowHeight}px` : undefined}
                    maxH={rowHeight ? `${rowHeight}px` : undefined}
                >
                    {cells.map((cell) => (
                        <Table.Cell
                            key={cell.id}
                            {...fixedRowCellProps(rowHeight)}
                        >
                            <FlexRender cell={cell} />
                        </Table.Cell>
                    ))}
                </Table.Row>
            )}
        </Subscribe>
    );
}
const DataRow = memo(DataRowInner) as typeof DataRowInner;

const SkeletonRow = memo(function SkeletonRow({
    index,
    columns,
    rowHeight,
}: {
    index: number;
    columns: { id: string }[];
    rowHeight?: number;
}) {
    return (
        <Table.Row
            aria-hidden
            // Same fixed tr height as DataRow.
            h={rowHeight ? `${rowHeight}px` : undefined}
            maxH={rowHeight ? `${rowHeight}px` : undefined}
        >
            {columns.map((column, columnIndex) => (
                <Table.Cell key={column.id} {...fixedRowCellProps(rowHeight)}>
                    <Skeleton
                        variant='shine'
                        height='3'
                        my='0.5'
                        maxW='12rem'
                        width={
                            SKELETON_CELL_WIDTHS[
                                (index + columnIndex) %
                                    SKELETON_CELL_WIDTHS.length
                            ]
                        }
                    />
                </Table.Cell>
            ))}
        </Table.Row>
    );
});

function JobsTableInner<TData extends RowData>({
    data,
    columns,
    getRowId,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    totalRows,
    emptyView,
    sorting,
    onSortingChange,
    rowHeight,
    rowSelection,
    onRowSelectionChange,
}: {
    data: TData[];
    columns: JobsColumns<TData>;
    getRowId: (row: TData) => string;
    /** First load: every row is a skeleton. */
    isLoading: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    /** The whole filtered set's size (page 1's `total`), when known: the
     * virtualizer then spans the full listing — honest scrollbar, no
     * scroll-stop wall at the loaded edge — and every not-yet-loaded index
     * renders a skeleton while the cursor pages chain in. */
    totalRows?: number;
    emptyView: React.ReactNode;
    /** Controlled sort state — sorting is SERVER-SIDE: the owner maps this
     * to the query's order param and the listing restarts from page 1, so
     * the whole filtered set is sorted, never just the loaded rows. The
     * table itself never reorders data (manualSorting). */
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
    /** Fixed row height in px (legacy-ui Table's rowHeight): cells drop
     * their vertical padding and center content vertically. Also sizes the
     * virtualizer's estimate. */
    rowHeight: number;
    /** Controlled row selection (keyed by getRowId) — supply both to
     * enable it; the owner renders the checkbox column and action bar. */
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}) {
    // The table's wrapper: the x-overflow container, and the anchor the
    // virtualizer measures its scrollMargin from.
    const listRef = useRef<HTMLDivElement>(null);
    // The table's top in the shell scroller's content coordinates. Measured
    // per render (cheap: two rect reads) because content ABOVE the table
    // changes height asynchronously — the retirement banner mounts after
    // its query lands — and a stale margin would shift every virtual
    // offset.
    const [scrollMargin, setScrollMargin] = useState(0);
    useLayoutEffect(() => {
        const el = listRef.current;
        const scroller = getShellScroller();
        if (!el || !scroller) return;
        const next =
            el.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top +
            scroller.scrollTop;
        setScrollMargin((prev) => (Math.abs(prev - next) < 1 ? prev : next));
    });

    const table = useTable({
        features: standardTableFeatures,
        data,
        columns,
        state: { sorting, rowSelection: rowSelection ?? EMPTY_ROW_SELECTION },
        onSortingChange,
        onRowSelectionChange,
        enableRowSelection: !!onRowSelectionChange,
        getRowId,
        manualSorting: true,
        enableMultiSort: false,
        enableSortingRemoval: false,
    });
    const rows = table.getRowModel().rows;
    const isEmpty = !isLoading && rows.length === 0;

    // Indices past rows.length render skeletons: the whole viewport during
    // the first load, then the rest of the listing. With the true total the
    // scroll range never ends at the loaded edge — the "fling, slam into
    // the end, wait for the next page to grow the scrollbar" stutter was
    // exactly that wall. Math.max guards a stale/smaller total; no total
    // (never sent past page 1's shape changing) falls back to a fixed
    // runway.
    const virtualCount = isLoading
        ? INITIAL_SKELETON_ROWS
        : hasNextPage
          ? Math.max(totalRows ?? 0, rows.length + PHANTOM_ROWS)
          : rows.length;

    // Row pitch: rows are pinned to rowHeight with border-box cells under
    // border-collapse, so the 1px separator renders INSIDE the box and the
    // stacking pitch is exactly rowHeight. (A +1 for the border here drifts
    // every virtual offset by 1px per row — measured, not assumed.)
    const rowPitch = rowHeight;

    // Pre-rendered rows OUTSIDE the window, both directions, sized to the
    // live table height. (Falls back to the floor on the pre-mount first
    // render.)
    const overscanRows = Math.max(
        OVERSCAN_MIN_ROWS,
        Math.ceil((window.innerHeight / rowPitch) * OVERSCAN_VIEWPORTS),
    );

    // A programmatic clear (export success, action-bar close) empties the
    // controlled rowSelection without touching v9's shift-range anchor,
    // which lives on the table instance — the next Shift-click would range
    // against a row selected before the clear. resetRowSelection() is the
    // public API that clears both; the non-empty→empty transition guard
    // keeps its controlled write-back (a fresh {}) from re-firing this.
    const prevSelectionRef = useRef(rowSelection);
    useEffect(() => {
        const prev = prevSelectionRef.current;
        prevSelectionRef.current = rowSelection;
        if (
            rowSelection &&
            prev &&
            Object.keys(rowSelection).length === 0 &&
            Object.keys(prev).length > 0
        ) {
            table.resetRowSelection();
        }
    }, [rowSelection, table]);

    // Fresh listing rebuilding from skeletons (cold cache): the panel
    // belongs at the top, or the old scroll offset lands mid-list and
    // chain-fires page fetches. Cached listings deliberately DON'T reset:
    // sorting/filter changes remount this component (the owner's key) and
    // scroll to top from MyJobs — above the remount boundary — while
    // Back-navigation must keep the shell ScrollRestoration's restored
    // offset (this passive effect would run after the restore and zero it).
    useEffect(() => {
        if (isLoading) scrollShellToTop();
    }, [isLoading]);

    // Fixed-size virtualization: every row is pinned to rowPitch, so the
    // estimate IS the truth and rows are deliberately NOT measured —
    // per-row measureElement forced a layout read per mounted row on every
    // scroll commit, pure overhead at fixed heights.
    // The SHELL PANEL is the scroll element: the table has no scroller of
    // its own — the page grows inside the panel, like every other page.
    // scrollMargin maps panel scroll offsets into table-local row offsets.
    const virtualizer = useVirtualizer({
        getScrollElement: getShellScroller,
        scrollToFn: NEVER_SCROLL,
        count: virtualCount,
        estimateSize: () => rowPitch,
        overscan: overscanRows,
        scrollMargin,
    });
    const virtualItems = virtualizer.getVirtualItems();

    // Infinite trigger: nearing the last loaded row asks for the next page.
    const lastVirtualIndex = virtualItems.at(-1)?.index ?? 0;
    useEffect(() => {
        if (
            !isLoading &&
            hasNextPage &&
            !isFetchingNextPage &&
            lastVirtualIndex >= rows.length - PREFETCH_BUFFER_ROWS
        ) {
            fetchNextPage();
        }
    }, [
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        lastVirtualIndex,
        rows.length,
        fetchNextPage,
    ]);

    // Virtual offsets include scrollMargin — subtract it so the offset
    // stays table-local. Applied as a TRANSFORM on the row window
    // (not spacer rows): a spacer resize relayouts and repaint-invalidates
    // the whole 120k-px panel on every scroll commit, and rasterization
    // falling behind paints the page as blank tiles mid-fling. A transform
    // never touches layout, so tiles stay valid and the fling stays
    // painted; the wrapper below holds the listing's full height, which
    // only changes when the DATA does.
    const offsetTop = (virtualItems[0]?.start ?? scrollMargin) - scrollMargin;
    const HEADER_HEIGHT = 44;
    // The wrapper is border-box with a 1px border all around — without the
    // 2px the content box runs short and the coerced overflow-y grows a 2px
    // internal scroll sliver at the listing's end.
    const WRAPPER_BORDERS_Y = 2;
    const listHeight = isEmpty
        ? undefined
        : HEADER_HEIGHT + virtualizer.getTotalSize() + WRAPPER_BORDERS_Y;

    // Identity-stable across scroll renders (table state unchanged), so the
    // memoized skeleton rows bail out too.
    const leafColumns = table.getVisibleLeafColumns();

    return (
        <Box
            ref={listRef}
            // No y-scroller: rows ride the shell panel's scroll. x stays an
            // internal overflow for narrow viewports. The explicit height
            // is the whole virtual listing — the table itself only ever
            // holds the rendered window.
            overflowX='auto'
            style={{ height: listHeight }}
            border='1px solid'
            borderColor='border'
            borderRadius='5px'
            bg='bg.panel'
            // overflow-anchor off: the browser's scroll anchoring fights the
            // virtualizer's offset math on upward scrolls. Quiet scrollbars
            // for the x-bar.
            css={SCROLLER_CSS}
        >
            <Table.Root
                // Stock table styling + legacy-ui's shared overrides — the
                // same theming enterprise tables get (semantic tokens, so
                // dark mode resolves correctly). The remote's light-only
                // `ionq` variant is deliberately NOT used here. No
                // stickyHeader: under window virtualization the wrapper is
                // the (never-y-scrolling) sticky container, so the prop
                // would be inert — the header scrolls away by design.
                aria-label='My Jobs'
                aria-busy={isLoading || isFetchingNextPage || undefined}
                // 44px header: the shared header Flex pads 12px each side —
                // pin the row, cells and the Flex child to 44px and let the
                // Flex's align-center do the vertical centering.
                css={{
                    '& thead tr': {
                        height: '44px',
                        maxHeight: '44px',
                    },
                    '& thead th': {
                        height: '44px',
                        maxHeight: '44px',
                        // One-line labels ("Estimated Cost"), as in the
                        // donor's nowrap ScrollArea; the table x-scrolls.
                        whiteSpace: 'nowrap',
                    },
                    '& thead th > *': {
                        height: '44px',
                        paddingBlock: 0,
                    },
                    // Row separators as an inset shadow, not a collapsed
                    // border: collapsed borders are painted by the table,
                    // and on the transformed row window they can drop out
                    // after a few pages. Same 1px inside the border-box
                    // cell, so rowPitch is unchanged.
                    '& tbody td': {
                        borderBottomWidth: 0,
                        boxShadow: 'inset 0 -1px 0 {colors.border}',
                    },
                }}
            >
                <Table.Header>
                    <TableHeaderRows table={table} isLoading={isLoading} />
                </Table.Header>
                <Table.Body
                    // The row window rides a compositor-only transform (its
                    // own layer via willChange) — scrolling never changes
                    // layout, only which rows render and this offset.
                    style={{
                        transform: `translateY(${offsetTop}px)`,
                        willChange: 'transform',
                    }}
                >
                    {!isEmpty &&
                        virtualItems.map((virtualRow) => {
                            const row = rows[virtualRow.index];
                            if (!row) {
                                return (
                                    <SkeletonRow
                                        key={`skeleton-${virtualRow.index}`}
                                        index={virtualRow.index}
                                        columns={leafColumns}
                                        rowHeight={rowHeight}
                                    />
                                );
                            }
                            return (
                                <DataRow
                                    key={row.id}
                                    row={row}
                                    // Memoized: stable across scroll renders.
                                    cells={row.getVisibleCells()}
                                    rowHeight={rowHeight}
                                />
                            );
                        })}
                </Table.Body>
            </Table.Root>
            {/* Donor parity (IonTable): the empty view is a SIBLING of the
                table, not a row — a row would carry the ionq variant's
                pointer cursor + hover highlight, and its width would be the
                table grid's, not the pane's. */}
            {isEmpty && emptyView}
        </Box>
    );
}

// Memoized like the rows: the owner re-renders on every visible-range
// change (counter state), and that must not re-render the whole table —
// every prop the owner passes is referentially stable between scrolls.
export const JobsTable = memo(JobsTableInner) as typeof JobsTableInner;
